import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import LoadingScreen from '~/components/LoadingScreen';
import { RouteConfig } from '~/routes/routeConfig';

const redirectDestinations = {
  CARD_CREATE: (resourceId: string) => `${RouteConfig.AccountsCards.buildLink()}/${resourceId}`,

  CARD_EDIT_SETTINGS: (resourceId: string) => `${RouteConfig.AccountsCards.buildLink()}/${resourceId}/settings`,

  CARD_SHOW_NUMBERS: (resourceId: string) => `${RouteConfig.AccountsCards.buildLink()}/${resourceId}`,

  CARD_ACTIVATE: (resourceId: string) => `${RouteConfig.AccountsCards.buildLink()}/${resourceId}`,

  CARD_RESUME: (resourceId: string) => `${RouteConfig.AccountsCards.buildLink()}/${resourceId}`,

  CARD_SHOW_PHYSICAL_CARD_PIN: (resourceId: string) => `${RouteConfig.AccountsCards.buildLink()}/${resourceId}`,

  ACCOUNT_MEMBERSHIP_CREATE: () => `${RouteConfig.SettingsTeam.buildLink()}/`,

  ACCOUNT_MEMBERSHIP_UPDATE: () => `${RouteConfig.SettingsTeam.buildLink()}/`,
} as const;

const redirectDestinationKeys = Object.keys(redirectDestinations) as (keyof typeof redirectDestinations)[];

const redirectParamsSchema = z.object({
  to: z.enum(
    redirectDestinationKeys as [
      (typeof redirectDestinationKeys)[number],
      ...(typeof redirectDestinationKeys)[number][],
    ],
  ),
  resourceId: z.string(),
});

/**
 * @component ActionConsentCallbackRedirect
 * @description
 * An Action Consent is a process where a user must provide explicit
 * authorization for a specific operation (e.g. modifying credit card settings).
 *
 * This component serves as a routing gateway after a user accepts a consent and is
 * redirected back from the backend (e.g., Swan). Its sole purpose is to handle this
 * callback, parse URL query parameters, and securely navigate the user to the
 * appropriate final destination within the frontend application.
 *
 * It securely constructs a redirect path using two URL query parameters:
 * - **to**: A string key from a predefined list (enum) that specifies the
 * destination page (e.g., 'CARD_CREATE'). This ensures the redirect is safe
 * and predictable.
 * - **resourceId**: The unique identifier for the specific resource (e.g., a
 * card ID) to be included in the final URL.*
 * @example
 * Flow: User clicks "Create Card" -> User is shown a consent screen -> User accepts ->
 * Backend acknoledges -> Backend redirects to `/action-consent/callback?to=CARD_CREATE&resourceId=123` ->
 * This component mounts, validates params, and navigates to `/accounts/cards/123`.
 *
 */
const ActionConsentCallbackRedirect = () => {
  const [searchParams] = useSearchParams();
  const to = searchParams.get('to');
  const resourceId = searchParams.get('resourceId');
  const navigate = useNavigate();

  useEffect(() => {
    const validationResult = redirectParamsSchema.safeParse({ to, resourceId });

    if (validationResult.success) {
      const { to: destinationKey, resourceId: id } = validationResult.data;

      const pathBuilder = redirectDestinations[destinationKey];
      const finalPath = pathBuilder(id);

      navigate(finalPath, { replace: true });
    } else {
      console.error('Invalid redirect parameters:', validationResult.error.flatten());
      navigate('/', { replace: true });
    }
  }, [navigate, to, resourceId]);

  return <LoadingScreen dataTestId="redirect" />;
};

export default ActionConsentCallbackRedirect;
